import moment from "moment";
import { timeHelper } from "@/screens/StudentData/utils/timeHelper";

export const joinWithAnd = (arr) => {
  if (arr.length === 0) {
    return "";
  }

  if (arr.length === 1) {
    return arr[0];
  }

  if (arr.length === 2) {
    return arr.join(" & ");
  }

  return arr.slice(0, -1).join(", ") + " & " + arr[arr.length - 1];
};

export function getDateRangeFromTime(timeString) {
  const duration = timeHelper(timeString);
  const endDate = moment().toDate();
  const startDate = moment().subtract(duration).startOf("day").toDate();

  return {
    startDate,
    endDate,
    durationHours: duration.asHours(),
  };
}

export function getStartDate(timePeriod) {
  const duration = timeHelper(timePeriod);
  return moment().subtract(duration).startOf("day").toDate();
}

export function chunkArray(arr, size = 500) {
  const output = [];

  for (let i = 0; i < arr.length; i += size) {
    output.push(arr.slice(i, i + size));
  }

  return output;
}

export function getCurrentAndNextPeriodDates(currentPeriodKey) {
  if (!currentPeriodKey) {
    return null;
  }
  const periods = [
    "month",
    "3 months",
    "6 months",
    "year",
    "2 years",
    "4 years",
  ];
  const idx = periods.indexOf(currentPeriodKey);
  const nextPeriod =
    idx >= 0 && idx < periods.length - 1 ? periods[idx + 1] : null;

  const startDateA = getStartDate(currentPeriodKey);
  const startDateB = nextPeriod ? getStartDate(nextPeriod) : null;

  return { startDateA, startDateB, nextPeriod };
}

export function createWellnessMerger() {
  const seenIds = new Set();
  const seenUserIdsMap = new Map();

  function mergeWellnessData(existing, incoming) {
    const incomingRaw = (incoming && incoming.raw) || [];
    const incomingByUserId = (incoming && incoming.byUserId) || {};

    if (!existing) {
      const newRaw = [];
      const newByUserId = {};

      for (let i = 0; i < incomingRaw.length; i++) {
        const doc = incomingRaw[i];
        const id = doc._id.toString();
        if (!seenIds.has(id)) {
          newRaw.push(doc);
          seenIds.add(id);
        }
      }

      for (const userId in incomingByUserId) {
        const entries = incomingByUserId[userId] || [];
        const userSet = new Set();
        const cleanEntries = [];

        for (let i = 0; i < entries.length; i++) {
          const doc = entries[i];
          const id = doc._id.toString();
          if (!userSet.has(id)) {
            cleanEntries.push(doc);
            userSet.add(id);
            seenIds.add(id);
          }
        }

        newByUserId[userId] = cleanEntries;
        seenUserIdsMap.set(userId, userSet);
      }

      return { raw: newRaw, byUserId: newByUserId };
    }

    const raw = existing.raw || (existing.raw = []);
    const byUserId = existing.byUserId || (existing.byUserId = {});

    if (seenIds.size === 0) {
      for (let i = 0; i < raw.length; i++) {
        seenIds.add(raw[i]._id.toString());
      }

      for (const userId in byUserId) {
        const userEntries = byUserId[userId] || [];
        const userSet = new Set();

        for (let i = 0; i < userEntries.length; i++) {
          userSet.add(userEntries[i]._id.toString());
        }
        seenUserIdsMap.set(userId, userSet);
      }
    }

    for (let i = 0; i < incomingRaw.length; i++) {
      const doc = incomingRaw[i];
      const id = doc._id.toString();
      if (!seenIds.has(id)) {
        raw.push(doc);
        seenIds.add(id);
      }
    }

    for (const userId in incomingByUserId) {
      const newEntries = incomingByUserId[userId] || [];
      let existingEntries = byUserId[userId];
      let seenUserIds = seenUserIdsMap.get(userId);

      if (!existingEntries) {
        existingEntries = [];
        seenUserIds = new Set();
        byUserId[userId] = existingEntries;
        seenUserIdsMap.set(userId, seenUserIds);
      }

      for (let i = 0; i < newEntries.length; i++) {
        const doc = newEntries[i];
        const id = doc._id.toString();

        if (!seenUserIds.has(id)) {
          existingEntries.push(doc);
          seenUserIds.add(id);
          seenIds.add(id);
        }
      }
    }

    return { raw, byUserId };
  }

  mergeWellnessData.reset = () => {
    seenIds.clear();
    seenUserIdsMap.clear();
  };

  mergeWellnessData.stats = () => ({
    seenCount: seenIds.size,
    usersTracked: seenUserIdsMap.size,
  });

  return mergeWellnessData;
}

export function filterClassStudents(classes, filteredEmails) {
  const allowed = new Set(filteredEmails);

  return classes.map((cls) => ({
    ...cls,
    students: cls.students.filter((email) => allowed.has(email)),
  }));
}

function buildPinnedSet(pinnedFromHook) {
  const s = new Set();
  if (!pinnedFromHook) {
    return s;
  }

  for (let i = 0; i < pinnedFromHook.length; i++) {
    const e = pinnedFromHook[i]?.student?.email;
    if (e) {
      s.add(e);
    }
  }

  return s;
}

function normalizedOptimisticPins(optimisticPins) {
  const map = new Map();
  if (!optimisticPins) {
    return map;
  }

  for (const [email, val] of Object.entries(optimisticPins)) {
    if (val === true) {
      map.set(email, 1);
    } else if (val === false) {
      map.set(email, -1);
    } else {
      map.set(email, 0);
    }
  }

  return map;
}

function buildLevelPriority(levelToFlag, flagPriority) {
  const cache = new Map();

  if (!levelToFlag || !flagPriority) {
    return cache;
  }

  for (const [levelStr, flag] of Object.entries(levelToFlag)) {
    const level = Number(levelStr);
    cache.set(level, flagPriority[flag] ?? 0);
  }

  return cache;
}

// Schwartzian Transform
export function sortStudentList(
  filtered,
  pinnedStudentsFromHook,
  optimisticPins,
  levelToFlag,
  flagPriority,
  selectedStudent,
  studentFlags
) {
  const pinnedSet = buildPinnedSet(pinnedStudentsFromHook);
  const optPins = normalizedOptimisticPins(optimisticPins);
  const levelPriority = buildLevelPriority(levelToFlag, flagPriority);
  const decorated = new Array(filtered.length);

  for (let i = 0; i < filtered.length; i++) {
    const item = filtered[i];
    const email = item.name;
    const isSelected = email === selectedStudent ? 1 : 0;
    const pinnedFromHook = pinnedSet.has(email) ? 1 : 0;
    const opt = optPins.has(email) ? optPins.get(email) : 0;
    const pinned = opt === -1 ? 0 : Math.max(pinnedFromHook, opt === 1 ? 1 : 0);
    const level =
      (studentFlags && studentFlags[email] && studentFlags[email].level) || 1;
    const flagPrio = levelPriority.has(level)
      ? levelPriority.get(level)
      : flagPriority[levelToFlag[level]] ?? 0;
    const percent = typeof item.percent === "number" ? item.percent : 0;

    decorated[i] = {
      _orig: item,
      email,
      isSelected,
      pinned,
      flagPrio,
      percent,
    };
  }

  decorated.sort((x, y) => {
    // 1) selected first
    if (x.isSelected !== y.isSelected) {
      return y.isSelected - x.isSelected;
    }

    // 2) pinned first
    if (x.pinned !== y.pinned) {
      return y.pinned - x.pinned;
    }

    // 3) lower flagPrio first
    if (x.flagPrio !== y.flagPrio) {
      return x.flagPrio - y.flagPrio;
    }

    // 4) percent descending
    if (x.percent !== y.percent) {
      return y.percent - x.percent;
    }

    // 5) final stable tie-breaker: alphabetical email (deterministic)
    if (x.email < y.email) {
      return -1;
    }

    if (x.email > y.email) {
      return 1;
    }

    return 0;
  });

  return decorated.map((d) => d._orig);
}
